import {
  Typography,
  Box,
  Stack,
  Grid,
  Button,
  FormHelperText,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useState, useRef, useEffect, useMemo } from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import CustomDropzone from "global/components/CustomDropzone/CustomDropzone";
import DropzoneBody, {
  dropzoneSurfaceSx,
} from "global/components/CustomDropzone/DropzoneBody";
import { KeyboardArrowUp, Add, CloudUpload } from "@mui/icons-material";
import DraggableChip from "./DraggableChip";
import MyTemplates from "screens/Templates/MyTemplates";
import { embedEditorRequestId, isEmbedEditorMode } from "embed/EmbedEditorMode";
import { removeEmbedDocument } from "embed/embedService";
import { AppModal } from "global/components/AppModal";
import {
  handleErrorMessage,
  isTruthy,
  openErrorNotification,
} from "helpers/methods";
import { getEnvelopeId, getUploadDocumentLimit } from "../SignRequestService";
import { uploadFiles, triggerAutoDetectFields } from "../SignRequestHelper";
import { RequestFile } from "models/interfaces";
import { RootState } from "utils/store";
import {
  removeFileFromSignRequest,
  setReduxSignRequest,
  setErrors,
  setLoading,
  setUploadedFileNamesDelete,
  setReduxSelectedFile,
  setReduxSelectedParticipant,
  moveFileSignRequest,
  selectScreen,
  addFileInSignRequest,
  setUploadedFileNames,
  updateFileInSignRequest,
  setAssignExistingFields,
  updateFileWithFields,
} from "redux/appSlice";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import strings from "global/constants/StringConstants";
import { focusRing } from "theme/colors";
import DescriptionIcon from "@mui/icons-material/Description";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import StrictModeDroppable from "global/components/StrictModeDroppable/StrictModeDroppable";
import InfoHelper from "global/components/InfoHelperText/InfoHelper";
import { hasAccessTo } from "layouts/Config";
import { useFeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import UpgradeModal from "global/components/UpgradeModal/UpgradeModal";
import MaintenanceBanner from "global/components/MaintenanceBanner/MaintenanceBanner";
import { useLocation } from "react-router-dom";
import viewpaths from "global/constants/ViewPathConstants";
import CloudStoragePicker from "global/components/CloudStoragePicker/CloudStoragePicker";
import { CloudUploadResponse } from "global/components/CloudStoragePicker/types";
import { initialSelectedFile } from "Context";
import { tourAttr } from "tour";
import bulkTokens from "../BulkSendSigners.tokens";
import {
  CloseIcon as BulkCloseIcon,
  DragDotsIcon,
} from "./BulkSendSignersIcons";

/**
 * Sweeping background fill shown while a bulk-upload chip's file is still
 * uploading — purely a loading indicator. It grows from a small sliver up to
 * ~92% and is clipped by the chip's own rounded shape (the chip sets
 * `overflow: hidden`); the chip removes it entirely once the file is done, so
 * a finished chip returns to its normal resting look.
 */
const ChipUploadFill = ({ color }: { color: string }) => {
  const [progress, setProgress] = useState(8);
  useEffect(() => {
    const id = window.setInterval(() => {
      setProgress((prev) =>
        prev >= 92 ? 92 : Math.min(prev + Math.random() * 12, 92),
      );
    }, 700);
    return () => window.clearInterval(id);
  }, []);
  return (
    <Box
      aria-hidden="true"
      sx={{
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: `${progress}%`,
        backgroundColor: color,
        transition: "width 0.3s ease-in-out",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
};

const UploadDocuments = ({
  updateTemplate,
  isNewTemplate,
  bulk,
  onTemplateSelect,
}: {
  updateTemplate?: boolean;
  /**
   * Handle "Choose Template" here instead of navigating to `/use/template`.
   * Bulk send needs the chosen template to stay inside its own wizard; a
   * caller that omits this keeps today's behaviour exactly.
   */
  onTemplateSelect?: (template: any) => void;
  isNewTemplate?: boolean;
  /** Render the Bulk Send design variant (`Bulk Send.dc.html`, step 1). */
  bulk?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const { signRequest, isRequest, errors, uploadedFileNames, chipLoader } =
    useAppSelector(
    (state: RootState) => state.app,
  );
  const location = useLocation();
  const isSelfSign = location.pathname === viewpaths.createSelfSignRequestPath;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [docLimit, setDocLimit] = useState(0);
  const userAccess = true;
  const theme = useTheme();
  const isMobile = useAppSelector(selectScreen);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [maintenanceMsg, setMaintenanceMsg] = useState<string | undefined>();

  const multiDocState = useFeatureState(FeatureKeys.ENVELOPE_MULTI_DOCUMENT);
  const isMultiDocAvailable =
    multiDocState.status === strings.FEATURE_STATUS_AVAILABLE;
  const isMultiDocBlocked =
    signRequest.files.length >= 1 && !isMultiDocAvailable;
  const multiDocTooltip = isMultiDocBlocked
    ? multiDocState.status === "not-in-plan"
      ? "Uploading multiple documents is included in higher plans. Upgrade to enable."
      : (multiDocState as any).disabledMessage ?? "Uploading multiple documents is temporarily unavailable."
    : "";

      const safeDocLimit =
    Number.isFinite(docLimit) && docLimit > 0 ? docLimit : 0;
  const isDocLimitReached =
    safeDocLimit > 0 && signRequest.files.length >= safeDocLimit;
  const docLimitReachedTooltip = isDocLimitReached
    ? `Document limit reached (${signRequest.files.length}/${safeDocLimit}). Remove a document to upload another.`
    : safeDocLimit === 0
      ? "Document limit is loading. Please try again in a moment."
      : "";

  const triggerMultiDocGate = () => {
    if (multiDocState.status === "not-in-plan") {
      setUpgradeOpen(true);
    } else {
      setMaintenanceMsg((multiDocState as any).disabledMessage);
      setMaintenanceOpen(true);
    }
  };

  const templateUseState = useFeatureState(FeatureKeys.TEMPLATE_USE);
  const isTemplateUseAvailable =
    templateUseState.status === strings.FEATURE_STATUS_AVAILABLE;
  const templateUseTooltip =
    templateUseState.status === strings.FEATURE_STATUS_ADMIN_DISABLED
      ? ((templateUseState as any).disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP)
      : templateUseState.status === strings.FEATURE_STATUS_NOT_IN_PLAN
        ? strings.FEATURE_GATE_UPSELL_TOOLTIP
        : "";
  // hasAccessTo(strings.resources.sign, strings.sendPermission) ||
  // hasAccessTo(strings.resources.template, strings.addPermission);

  useEffect(() => {
    uploadDocumentsLimit();
  }, []);

  const uploadDocumentsLimit = async () => {
    try {
      dispatch(setLoading(true));
      const res = await getUploadDocumentLimit();
      setDocLimit(res);
      dispatch(setLoading(false));
    } catch (error: any) {
      dispatch(setLoading(false));
      handleErrorMessage(error);
    }
  };

  const hasMyRequestsAddPermission = useMemo(
    () => hasAccessTo(strings.resources.sign, strings.sendPermission),
    [],
  );

  const hasMyTemplateAddPermission = useMemo(
    () => hasAccessTo(strings.resources.template, strings.addPermission),
    [],
  );

  const hasMyTemplateUpdatePermission = useMemo(
    () => hasAccessTo(strings.resources.template, strings.updatePermission),
    [],
  );

  const hasSendOrUploadAccess = isRequest
    ? hasMyRequestsAddPermission
    : updateTemplate
      ? hasMyTemplateUpdatePermission
      : isNewTemplate
        ? hasMyTemplateAddPermission
        : false;

  const dropzoneTooltip = multiDocTooltip || docLimitReachedTooltip;
  const isDropzoneDisabled =
    !userAccess ||
    isDocLimitReached ||
    !hasSendOrUploadAccess ||
    isMultiDocBlocked;

  const checkFileSizeAndFormat = (
    file: File,
  ): { isValid: boolean; errorType: string } => {
    const existingFiles = signRequest.files.map(
      (f: any) => f.name || f.fileName,
    );
    if (existingFiles.includes(file.name)) {
      return { isValid: false, errorType: "duplicate" };
    }
    if (uploadedFileNames && uploadedFileNames.includes(file.name)) {
      return { isValid: false, errorType: "duplicate" };
    }
    const maxFileSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxFileSize) {
      return { isValid: false, errorType: "size" };
    }
    const acceptedFiles = [
      ".jpeg",
      ".jpg",
      ".png",
      ".gif",
      ".bmp",
      ".tiff",
      ".pdf",
      ".doc",
      ".docx",
      ".txt",
      ".xls",
      ".xlsx",
      ".heic",
    ];
    const fileExtension = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    if (!acceptedFiles.includes(fileExtension)) {
      return { isValid: false, errorType: "format" };
    }

    return { isValid: true, errorType: "" };
  };

  const processUploadFiles = (files: File[]) => {
    if (
      !isMultiDocAvailable &&
      signRequest.files.length + files.length > 1
    ) {
      triggerMultiDocGate();
      return;
    }
    const currentFileCount = signRequest.files.length;
    const remainingSlots = safeDocLimit - currentFileCount;
    if (remainingSlots <= 0) {
      openErrorNotification(
        safeDocLimit === 0
          ? "Document limit is still loading. Please try again in a moment."
          : `Upload limit reached. You can upload a maximum of ${safeDocLimit} documents.`,
      );
      return;
    }
    const filesToProcess = files.slice(0, remainingSlots);
    const exceededFiles = files.slice(remainingSlots);

    const validFiles: File[] = [];
    const duplicateFiles: string[] = [];
    const invalidFiles: string[] = [];
    const fileSizeExceededFiles: string[] = [];
    filesToProcess.forEach((file) => {
      const validation = checkFileSizeAndFormat(file);

      if (validation.isValid) {
        validFiles.push(file);
      } else {
        switch (validation.errorType) {
          case "duplicate":
            duplicateFiles.push(file.name);
            break;
          case "size":
            fileSizeExceededFiles.push(file.name);
            break;
          case "format":
            invalidFiles.push(file.name);
            break;
        }
      }
    });
    let errorMessage = "";

    if (exceededFiles.length > 0) {
      errorMessage += `Only ${remainingSlots} file(s) can be uploaded due to the limit of ${safeDocLimit} documents. ${exceededFiles.length} file(s) were ignored.\n`;
    }

    if (duplicateFiles.length > 0) {
      errorMessage += `File(s) [${duplicateFiles.join(
        ", ",
      )}] already exist. Please try different files.\n`;
    }

    if (invalidFiles.length > 0) {
      errorMessage += `File(s) [${invalidFiles.join(
        ", ",
      )}] have an unsupported file format.\n`;
    }

    if (fileSizeExceededFiles.length > 0) {
      errorMessage += `File(s) [${fileSizeExceededFiles.join(
        ", ",
      )}] exceed the maximum allowed file size of 10MB.`;
    }

    if (errorMessage) {
      openErrorNotification(errorMessage);
    }

    if (validFiles.length > 0) {
      handleFileUpload(validFiles);
    }
  };

  const ensureEnvelopeId = async (): Promise<string> => {
    if (isTruthy(signRequest.id)) return signRequest.id;
    const res = await getEnvelopeId();
    const newId = res.message;
    dispatch(setReduxSignRequest({ ...signRequest, id: newId }));
    return newId;
  };

  const handleCloudFileUploaded = (
    _provider: string,
    fileName: string,
    response: CloudUploadResponse,
    cloudEnvelopeId: string,
  ) => {
    const existingNames = signRequest.files.map(
      (f: any) => f.name || f.fileName || f.unsignedDoc,
    );
    if (existingNames.includes(fileName)) {
      openErrorNotification(
        `File [${fileName}] already exists. Please try a different file.`,
      );
      return;
    }
    if (signRequest.files.length >= docLimit) {
      openErrorNotification(
        `Upload limit reached. You can upload a maximum of ${docLimit} documents.`,
      );
      return;
    }

    const placeholder: RequestFile = {
      ...initialSelectedFile,
      unsignedDoc: fileName,
      isUploading: false,
      unsignedDocUrl: response?.unsignedPdfUrl ?? "",
      /* The server's name for the stored file — a converted upload is renamed. */
      unsignedPdf: (response as any)?.unsignedPdf ?? fileName,
    };
    dispatch(addFileInSignRequest(placeholder));
    dispatch(setUploadedFileNames([fileName]));
    dispatch(setErrors({}));

    // Kick off AI field detection now (background) so the editor's Auto-place
    // button is ready when the doc is displayed. Editor keeps its own fallback.
    triggerAutoDetectFields(
      cloudEnvelopeId || signRequest.id,
      fileName,
      dispatch,
      placeholder.unsignedPdf,
      isRequest,
    );

    const backendFields = (response?.fields ?? []).filter(
      (field: any) => field.type !== "drop_down" && field.type !== "button",
    );
    if (backendFields.length > 0) {
      const enriched: RequestFile = {
        ...placeholder,
        fields: backendFields,
      };
      dispatch(
        setAssignExistingFields({
          fileName,
          fieldsExist: true,
        }),
      );
      dispatch(updateFileInSignRequest(enriched));
      dispatch(updateFileWithFields(enriched));
    }
  };

  const handleFileUpload = async (files: File[]) => {
    try {
      if (!files || files.length === 0) return;

      let envelopeId = signRequest.id;
      const idMatchesTemplateId =
        isTruthy(signRequest.id) &&
        isTruthy((signRequest as any).templateId) &&
        String(signRequest.id) === String((signRequest as any).templateId);
      if (
        !isTruthy(signRequest.id) ||
        idMatchesTemplateId ||
        (signRequest.files.length === 0 && !isTruthy(signRequest.id))
      ) {
        const res = await getEnvelopeId();
        envelopeId = res.message;
      }

      dispatch(
        setReduxSignRequest({
          ...signRequest,
          id: envelopeId,
        }),
      );
      dispatch(setErrors({}));
      uploadFiles(files, dispatch, isRequest, envelopeId);
    } catch (error: any) {
      handleErrorMessage(error);
    }
  };

  const handleDeleteFile = (index: number) => {
    const fileToDelete = signRequest.files[index];
    if (fileToDelete) {
      if (isEmbedEditorMode() && fileToDelete.unsignedPdf) {
        // The draft lives on the server in the embedded wizard, so the removal must reach it too.
        removeEmbedDocument(embedEditorRequestId(), {
          unsignedPdf: fileToDelete.unsignedPdf,
        }).catch((error: any) => handleErrorMessage(error));
      }
      dispatch(
        setUploadedFileNamesDelete(
          fileToDelete.unsignedPdf || fileToDelete.unsignedDoc || "",
        ),
      );
      dispatch(removeFileFromSignRequest(fileToDelete));
    }
  };

  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      processUploadFiles(Array.from(files));
    }
    event.target.value = "";
  };

  const handleChooseTemplateClick = () => {
    // dispatch(setReduxSignRequest(null));
    dispatch(setReduxSelectedParticipant(null));
    dispatch(setReduxSelectedFile(null));
    setIsTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = () => {
    setIsTemplateModalOpen(false);
  };

  /** Undefined unless the caller wants the template handled in place. */
  const handleTemplateSelected = onTemplateSelect
    ? (template: any) => {
        setIsTemplateModalOpen(false);
        onTemplateSelect(template);
      }
    : undefined;

  const handleOnDragEnd = (result: any) => {
    if (
      isTruthy(result?.source?.index) &&
      isTruthy(result?.destination?.index)
    ) {
      dispatch(
        moveFileSignRequest({
          fromIndex: result.source.index,
          toIndex: result.destination.index,
        }),
      );
    }
  };

  const getHeader = () => (
    <Stack direction={"column"} spacing={3}>
      <Stack direction={"row"} spacing={0.5}>
        <Typography
          variant={isMobile ? "subtitle2" : "h4"}
          color="text.primary"
          fontWeight={600}
        >
          Select documents to be signed
        </Typography>
      </Stack>

      <Stack
        direction="row"
        spacing={2}
        flexWrap="wrap"
        useFlexGap
        sx={{
          "&.MuiStack-root": {
            marginTop: isSelfSign ? 0 : isMobile ? 2 : 3,
          },
        }}
      >
        {isRequest && !isSelfSign && (
          <Tooltip
            describeChild
            title={!isTemplateUseAvailable ? templateUseTooltip : ""}
            arrow
            disableHoverListener={isTemplateUseAvailable}
          >
            <Box
              component="span"
              sx={{ display: "inline-flex" }}
              {...tourAttr("request.chooseTemplate")}
            >
              <Button
                variant="outlined"
                startIcon={<DescriptionIcon />}
                endIcon={
                  !isTemplateUseAvailable ? (
                    <LockOutlinedIcon
                      sx={{ fontSize: 16, color: "#9ca3af" }}
                    />
                  ) : undefined
                }
                onClick={handleChooseTemplateClick}
                disabled={!hasSendOrUploadAccess || !isTemplateUseAvailable}
                size={isMobile ? "small" : "medium"}
                sx={{
                  // minWidth: isMobile ? "100%" : "auto",
                  fontSize: isMobile ? "12px" : "14px",
                  padding: isMobile ? "6px 10px" : "8px 16px",
                  ...(isMobile && {
                    "&.MuiButtonBase-root": {
                      minWidth: "unset !important",
                      minHeight: "36px !important",
                      height: "36px !important",
                      width: "auto !important",
                      fontSize: "0.75rem",
                      padding: "0.25rem 0.625rem",
                    },
                  }),
                }}
              >
                Choose Template
              </Button>
            </Box>
          </Tooltip>
        )}

        <Box
          component="span"
          sx={{ display: "inline-flex" }}
          {...tourAttr("request.fromCloud")}
        >
          <CloudStoragePicker
            envelopeId={signRequest.id}
            ensureEnvelopeId={ensureEnvelopeId}
            onFileUploaded={handleCloudFileUploaded}
            setLoading={(loading) => dispatch(setLoading(loading))}
            disabled={
              !hasSendOrUploadAccess ||
              signRequest.files.length >= docLimit ||
              isMultiDocBlocked
            }
            disabledTooltip={
              isMultiDocBlocked
                ? multiDocTooltip
                : signRequest.files.length >= docLimit
                  ? `Upload limit reached. Maximum ${docLimit} documents.`
                  : !hasSendOrUploadAccess
                    ? "You don't have permission to upload documents."
                    : ""
            }
          />
        </Box>
      </Stack>

      <input
        ref={fileInputRef}
        type="file"
        multiple={isMultiDocAvailable}
        accept=".jpeg,.jpg,.png,.gif,.bmp,.tiff,.pdf,.doc,.docx,.txt,.xls,.xlsx,.heic"
        onChange={handleFileInputChange}
        style={{ display: "none" }}
      />
    </Stack>
  );

  const getBody = () => (
    <Stack direction="column" spacing={3}>
      <Box>
        <Tooltip
          describeChild
          title={multiDocTooltip}
          disableHoverListener={!isMultiDocBlocked}
          arrow
        >
          <Box
            component="span"
            sx={{ display: "block" }}
            {...tourAttr("request.upload")}
          >
            <CustomDropzone
              innerBody={<DropzoneBody title="Drop your documents here" />}
              sx={dropzoneSurfaceSx({
                disabled: isDropzoneDisabled,
                error: !isTruthy(signRequest.files) && Boolean(errors.file),
              })}
              onAdd={processUploadFiles}
              maxFileSize={10 * 1024 * 1024}
              showAlerts={true}
              multipleFileUpload={isMultiDocAvailable}
              acceptedFiles={[
                ".jpeg",
                ".jpg",
                ".png",
                ".gif",
                ".bmp",
                ".tiff",
                ".pdf",
                ".doc",
                ".docx",
                ".txt",
                ".xls",
                ".xlsx",
                ".heic",
              ]}
              fileNames={uploadedFileNames}
              uniqueId="upload-documents"
              disabled={
                !userAccess ||
                signRequest.files.length >= docLimit ||
                !hasSendOrUploadAccess ||
                isMultiDocBlocked
              }
              uploadDocLimit={docLimit}
              isUploadDocLimit={true}
            />
          </Box>
        </Tooltip>
        {docLimit === 0 && !isTruthy(signRequest.files) && errors.file && (
          <FormHelperText error role="alert" sx={{ marginLeft: "14px !important" }}>
            {signRequest.files.length > 0 ? "" : errors.file}
          </FormHelperText>
        )}

        <Typography variant="subtitle2" mt={2}>
          Uploaded documents ({signRequest.files.length}/{safeDocLimit})
        </Typography>

        <DragDropContext onDragEnd={handleOnDragEnd}>
          <StrictModeDroppable droppableId="files" direction="horizontal">
            {(provided: any) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                }}
              >
                {signRequest.files &&
                  signRequest.files.map((file: RequestFile, index: number) => (
                    <Draggable
                      key={file.unsignedDoc}
                      draggableId={file.unsignedDoc}
                      index={index}
                    >
                      {(provided: any) => (
                        <Box
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            marginRight: 1,
                            display: "flex",
                            alignItems: "center",
                            cursor: "grab",
                          }}
                        >
                          <DraggableChip
                            file={file}
                            index={index}
                            dragHandleProps={provided.dragHandleProps}
                            onDelete={handleDeleteFile}
                            forceComplete={
                              signRequest.status === strings.draft &&
                              !!file.pageCount
                            }
                          />
                        </Box>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </Box>
            )}
          </StrictModeDroppable>
        </DragDropContext>
      </Box>
    </Stack>
  );

  // ---------------------------------------------------------------------------
  // Bulk Send design variant — `Bulk Send.dc.html`, step 1 "Select documents to
  // be signed". Reuses every handler above; only the presentation changes.
  // ---------------------------------------------------------------------------
  const u = bulkTokens.upload;
  const templateDisabled = !hasSendOrUploadAccess || !isTemplateUseAvailable;

  const renderBulkChooseTemplate = () => (
    <Tooltip
      describeChild
      title={!isTemplateUseAvailable ? templateUseTooltip : ""}
      arrow
      disableHoverListener={isTemplateUseAvailable}
    >
      {/* Matches the "From cloud" trigger: shared outlined-button theme + the
          same sx, so the two actions read as one set. */}
      <Box
        component="span"
        sx={{ display: "inline-flex" }}
        {...tourAttr("request.chooseTemplate")}
      >
        <Button
          variant="outlined"
          startIcon={<DescriptionOutlinedIcon />}
          onClick={handleChooseTemplateClick}
          disabled={templateDisabled}
          sx={{ textTransform: "none", fontSize: "14px" }}
        >
          Choose Template
        </Button>
      </Box>
    </Tooltip>
  );

  if (bulk) {
    return (
      <>
        <input
          ref={fileInputRef}
          type="file"
          multiple={isMultiDocAvailable}
          accept=".jpeg,.jpg,.png,.gif,.bmp,.tiff,.pdf,.doc,.docx,.txt,.xls,.xlsx,.heic"
          onChange={handleFileInputChange}
          style={{ display: "none" }}
        />
        <Box
          sx={{
            maxWidth: bulkTokens.page.maxWidth,
            mx: "auto",
            p: u.padding,
            boxSizing: "border-box",
          }}
        >
          <Box
            component="h1"
            sx={{
              m: 0,
              mb: u.titleMarginBottom,
              fontSize: u.titleFontSize,
              fontWeight: u.titleFontWeight,
              letterSpacing: u.titleLetterSpacing,
              color: u.titleColor,
            }}
          >
            Select documents to be signed
          </Box>

          {/* Templates and cloud drives need a signed-in Inkfree user; the embedded wizard has none. */}
          {!isEmbedEditorMode() && (
          <Box
            sx={{
              display: "flex",
              gap: u.btnRowGap,
              mb: u.btnRowMarginBottom,
            }}
          >
            {isRequest && !isSelfSign && renderBulkChooseTemplate()}
            <Box
              component="span"
              sx={{ display: "inline-flex" }}
              {...tourAttr("request.fromCloud")}
            >
              <CloudStoragePicker
                envelopeId={signRequest.id}
                ensureEnvelopeId={ensureEnvelopeId}
                onFileUploaded={handleCloudFileUploaded}
                setLoading={(loading) => dispatch(setLoading(loading))}
                disabled={
                  !hasSendOrUploadAccess ||
                  signRequest.files.length >= docLimit ||
                  isMultiDocBlocked
                }
                disabledTooltip={
                  isMultiDocBlocked
                    ? multiDocTooltip
                    : signRequest.files.length >= docLimit
                      ? `Upload limit reached. Maximum ${docLimit} documents.`
                      : !hasSendOrUploadAccess
                        ? "You don't have permission to upload documents."
                        : ""
                }
              />
            </Box>
          </Box>
          )}

          {/* Same surface as the Dashboard and every other upload in the app —
              swapped in for the old hand-rolled dashed box so this step (which
              always renders the "bulk" variant, see SignRequestComponent /
              SelfSignRequestComponent) stops drifting from the shared look. */}
          <Tooltip describeChild title={multiDocTooltip} disableHoverListener={!isMultiDocBlocked} arrow>
            <Box
              component="span"
              sx={{ display: "block" }}
              {...tourAttr("request.upload")}
            >
              <CustomDropzone
                innerBody={<DropzoneBody title="Drop your documents here" />}
                sx={dropzoneSurfaceSx({
                  disabled: isDropzoneDisabled,
                  error: !isTruthy(signRequest.files) && Boolean(errors.file),
                })}
                onAdd={processUploadFiles}
                maxFileSize={10 * 1024 * 1024}
                showAlerts={true}
                multipleFileUpload={isMultiDocAvailable}
                acceptedFiles={[
                  ".jpeg",
                  ".jpg",
                  ".png",
                  ".gif",
                  ".bmp",
                  ".tiff",
                  ".pdf",
                  ".doc",
                  ".docx",
                  ".txt",
                  ".xls",
                  ".xlsx",
                  ".heic",
                ]}
                fileNames={uploadedFileNames}
                uniqueId="bulk-upload-documents"
                disabled={
                  !userAccess ||
                  signRequest.files.length >= docLimit ||
                  !hasSendOrUploadAccess ||
                  isMultiDocBlocked
                }
                uploadDocLimit={docLimit}
                isUploadDocLimit={true}
              />
            </Box>
          </Tooltip>

          <Box sx={{ mt: u.listMarginTop }}>
            <Box
              sx={{
                fontSize: u.listLabelFontSize,
                fontWeight: u.listLabelFontWeight,
                color: u.listLabelColor,
                mb: u.listLabelMarginBottom,
              }}
            >
              Uploaded documents ({signRequest.files.length}/{safeDocLimit || 10})
            </Box>
            <DragDropContext onDragEnd={handleOnDragEnd}>
              <StrictModeDroppable
                droppableId="bulk-uploaded-files"
                direction="horizontal"
              >
                {(dropProvided: any) => (
                  <Box
                    ref={dropProvided.innerRef}
                    {...dropProvided.droppableProps}
                    sx={{ display: "flex", flexWrap: "wrap", gap: u.chipsGap }}
                  >
                    {(signRequest.files || []).map(
                      (file: RequestFile, index: number) => {
                        // The fill is purely a loading indicator: it sweeps only
                        // while the file is uploading. Once processed (has a
                        // `pageCount`) the chip drops the fill and returns to its
                        // normal resting look.
                        const uploading =
                          (!!file.isUploading || chipLoader) && !file.pageCount;
                        const draggableId =
                          file.unsignedDoc ||
                          file.unsignedPdf ||
                          `file-${index}`;
                        return (
                          <Draggable
                            key={draggableId}
                            draggableId={draggableId}
                            index={index}
                          >
                            {(dragProvided: any) => (
                              <Box
                                ref={dragProvided.innerRef}
                                {...dragProvided.draggableProps}
                                sx={{
                                  position: "relative",
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: u.chipGap,
                                  height: u.chipHeight,
                                  p: u.chipPadding,
                                  borderRadius: u.chipRadius,
                                  // While uploading, keep the base transparent so
                                  // only the sweeping fill shows behind it; the
                                  // resting colour returns once the upload done.
                                  backgroundColor: uploading
                                    ? "transparent"
                                    : u.chipBg,
                                  border: u.chipBorder,
                                  maxWidth: u.chipMaxWidth,
                                  cursor: "grab",
                                  userSelect: "none",
                                  "&:active": { cursor: "grabbing" },
                                }}
                              >
                                {uploading && (
                                  <ChipUploadFill
                                    color={theme.palette.primary.light}
                                  />
                                )}
                                {/* The drag handle (role="button") wraps only the dots
                                    and name, so the Remove button is not nested
                                    inside another control (WCAG 4.1.2). */}
                                <Box
                                  {...dragProvided.dragHandleProps}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: u.chipGap,
                                    minWidth: 0,
                                    "&:focus-visible": {
                                      outline: `2px solid ${focusRing}`,
                                      outlineOffset: "2px",
                                      borderRadius: u.chipRadius,
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      color: u.chipDotsColor,
                                      position: "relative",
                                      zIndex: 1,
                                    }}
                                  >
                                    <DragDotsIcon size={u.chipDotsIconSize} />
                                  </Box>
                                  <Box
                                    component="span"
                                    sx={{
                                      fontSize: u.chipNameFontSize,
                                      fontWeight: u.chipNameFontWeight,
                                      color: u.chipNameColor,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      minWidth: 0,
                                      position: "relative",
                                      zIndex: 1,
                                    }}
                                  >
                                    {file.unsignedDoc}
                                  </Box>
                                </Box>
                                <Box
                                  component="button"
                                  type="button"
                                  aria-label={`Remove ${file.unsignedDoc}`}
                                  onClick={(e: any) => {
                                    e.stopPropagation();
                                    handleDeleteFile(index);
                                  }}
                                  // Don't let a mousedown on delete start a drag.
                                  onMouseDown={(e: any) => e.stopPropagation()}
                                  // Keep Enter / Space on this button away from
                                  // the chip's drag-handle keyboard sensor.
                                  onKeyDown={(e: any) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                      e.stopPropagation();
                                    }
                                  }}
                                  sx={{
                                    width: u.chipRemoveSize,
                                    height: u.chipRemoveSize,
                                    p: 0,
                                    border: 0,
                                    background: "transparent",
                                    borderRadius: u.chipRemoveRadius,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    color: u.chipRemoveColor,
                                    flexShrink: 0,
                                    position: "relative",
                                    zIndex: 1,
                                    "&:hover": {
                                      backgroundColor: u.chipRemoveHoverBg,
                                    },
                                    "&:focus-visible": {
                                      outline: `2px solid ${focusRing}`,
                                      outlineOffset: "-2px",
                                    },
                                  }}
                                >
                                  <BulkCloseIcon size={u.chipRemoveIconSize} />
                                </Box>
                              </Box>
                            )}
                          </Draggable>
                        );
                      },
                    )}
                    {dropProvided.placeholder}
                  </Box>
                )}
              </StrictModeDroppable>
            </DragDropContext>
          </Box>
        </Box>

        <AppModal
          open={isTemplateModalOpen}
          onClose={handleCloseTemplateModal}
          title="Choose a template"
          // A template picker showing a folder/template grid needs more room
          // than the L step gives it — XL plus a taller body so more rows are
          // visible before the picker's own scroll area kicks in.
          size="xl"
          disableBodyPadding={isMobile}
          bodySx={isMobile ? undefined : { maxHeight: "78vh" }}
        >
          <MyTemplates
            isShowCreateBtn={false}
            isShowMoreIcon={false}
            isUseTemplate={true}
            handleCloseTemplateModal={handleCloseTemplateModal}
            onTemplateSelect={handleTemplateSelected}
            isMobile={isMobile}
          />
        </AppModal>
        <UpgradeModal
          open={upgradeOpen}
          featureKey={FeatureKeys.ENVELOPE_MULTI_DOCUMENT}
          onClose={() => setUpgradeOpen(false)}
        />
        <MaintenanceBanner
          open={maintenanceOpen}
          message={maintenanceMsg}
          onClose={() => setMaintenanceOpen(false)}
        />
      </>
    );
  }

  return (
    <>
      <CenteredAuthenticatedLayout>
        {getHeader()}
        {/*
          A `div`, not a `main`: this step is rendered inside SignRequest/
          SelfSignRequest, which App already wraps in `<main id="main-content">`.
          A `main` here produced a second, nested main landmark (WCAG 1.3.1).
        */}
        <Box
          sx={{
            flexGrow: 1,
            mt: 3,
            pb: 6,
          }}
        >
          {getBody()}
        </Box>
      </CenteredAuthenticatedLayout>
      <AppModal
        open={isTemplateModalOpen}
        onClose={handleCloseTemplateModal}
        ariaLabel="Choose a template"
        // A template picker showing a folder/template grid needs more room
        // than the L step gives it — XL plus a taller body so more rows are
        // visible before the picker's own scroll area kicks in.
        size="xl"
        disableBodyPadding={isMobile}
        bodySx={isMobile ? undefined : { maxHeight: "78vh" }}
      >
        <MyTemplates
          isShowCreateBtn={false}
          isShowMoreIcon={false}
          isUseTemplate={true}
          handleCloseTemplateModal={handleCloseTemplateModal}
          onTemplateSelect={handleTemplateSelected}
          isMobile={isMobile}
        />
      </AppModal>
      <UpgradeModal
        open={upgradeOpen}
        featureKey={FeatureKeys.ENVELOPE_MULTI_DOCUMENT}
        onClose={() => setUpgradeOpen(false)}
      />
      <MaintenanceBanner
        open={maintenanceOpen}
        message={maintenanceMsg}
        onClose={() => setMaintenanceOpen(false)}
      />
    </>
  );
};

export default UploadDocuments;
